// Communications.js — Settings → Communications.
//
// Organisation-level configuration for the automatic push notifications sent
// to staff and managers around clock in/out and breaks. Every notification is
// OFF by default; the message text is editable per organisation so wording can
// match local practice (and be translated) without a release.
//
// Storage: stockcontrol.organisations -> staff_notifications, alongside the
// existing organisation-level settings such as payroll_month_end.
//
// The backend evaluator (report_scheduler.js) reads these settings and decides
// what to send; nothing is sent from this page.
//
// Two modes, mirroring OrgSettings.js:
//   • Standalone (default)  — its own heading and Save button. The `communications`
//     route is kept intact even though the nav button has gone.
//   • Embedded (`embedded`) — heading and Save button hidden, because it now lives
//     as the "Communications" tab of the Settings dialog and is saved by that
//     dialog's "Save changes". The parent drives it through a ref:
//         const r = useRef(null);
//         <Communications ref={r} embedded onDirty={() => setHasChanges(true)} />
//         await r.current.save();     // resolves, or throws with a message
//     `save()` is a no-op unless the settings loaded AND something was edited, so
//     saving an untouched tab can never overwrite good wording with stale state.

import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";

// Embedded live in the hub below — the Action centre moved here from Head Office.
import ActionCentre from "./ActionCentre";
// Messages — per-staff message history and the send dialogue. Reads the
// unified pharmace_common_db.staff_messages store written by every send point
// in the app (see staff_messages.js on the backend).
import StaffMessages from "./StaffMessages";

const API = "https://api.pharmacegroup.co.uk";

function authHeaders() {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

// ---------------------------------------------------------------------------
// The notification catalogue. Keys are STABLE — they are what the backend
// evaluator switches on, so they must not be renamed once live. Adding a new
// notification is a matter of adding a row here and handling the key in the
// evaluator; the page needs no other change.
//
// `tokens` lists the placeholders the evaluator substitutes at send time. They
// are shown to the user, and unknown tokens are left untouched rather than
// blanked, so a typo is visible in the message instead of silently vanishing.
// ---------------------------------------------------------------------------
export const NOTIFICATIONS = [
  {
    key: "breakEndingSoon",
    audience: "Staff",
    label: "Send alert five minutes before break end",
    defaultText: "Your break is due to finish in five minutes.",
    tokens: ["{time}", "{name}", "{pharmacy}"],
    help: "Fires five minutes before the end of a break that has a recorded finish time. Nothing is sent for an open-ended break."
  },
  {
    key: "clockOutDue",
    audience: "Staff",
    label: "Send alert five minutes after scheduled clock-out time",
    defaultText: "Your normal logout time today is {time}. Please logout unless you are doing overtime, in which case logout at the end of your session.",
    tokens: ["{time}", "{name}", "{pharmacy}"],
    help: "Fires five minutes after the scheduled finish when the member of staff is still clocked in. {time} is their scheduled finish for the day."
  },
  {
    key: "clockInMissing",
    audience: "Staff",
    label: "Send alert five minutes after scheduled clock-in time",
    defaultText: "Your scheduled session was due to start at {time}. Please remember to login, and include an explanatory note.",
    tokens: ["{time}", "{name}", "{pharmacy}"],
    help: "Fires five minutes after the scheduled start when no clock-in has been recorded. {time} is their scheduled start."
  },
  {
    key: "pharmacistNotArrived",
    audience: "Manager",
    label: "Pharmacist not arrived",
    defaultText: "This is to alert you that your pharmacist {name} has not yet clocked in at {pharmacy}.",
    tokens: ["{name}", "{pharmacy}", "{time}"],
    help: "Fires five minutes after the pharmacist's scheduled start when no clock-in has been recorded. Sent to managers and group managers attached to that branch — not to the pharmacist."
  }
];

const TOKEN_HELP = {
  "{time}": "the relevant scheduled time for that person and day, as HH:MM",
  "{name}": "the staff member's name",
  "{pharmacy}": "the branch name"
};

const help = { fontSize: 11, color: "#6b7280", marginTop: 2, lineHeight: 1.4 };
const label = { fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: 0.3 };
const section = { border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px", marginBottom: 16, background: "#fff" };

const emptyState = () => {
  const o = {};
  for (const n of NOTIFICATIONS) o[n.key] = { enabled: false, text: n.defaultText };
  return o;
};

const StaffNotificationSettings = forwardRef(function StaffNotificationSettings({ embedded = false, onDirty } = {}, ref) {
  const [org, setOrg] = useState("");
  const [rows, setRows] = useState(emptyState);
  const [status, setStatus] = useState("loading");   // loading | ready | saving | saved | error
  const [error, setError] = useState(null);

  // Refs, not state, so the imperative save() always reads current values
  // rather than whatever was captured when the handle was created.
  const loadedRef = useRef(false);
  const dirtyRef  = useRef(false);
  const valuesRef = useRef({});
  valuesRef.current = { rows, org };

  useEffect(() => {
    (async () => {
      try {
        const isAll = (localStorage.getItem("organisationName") || "") === "Pharmace all";
        const body = isAll ? { organisationName: localStorage.getItem("bi_targetOrg") || "" } : {};
        const res = await fetch(`${API}/api/headoffice/communications/get`, {
          method: "POST", headers: authHeaders(), body: JSON.stringify(body)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        setOrg(data.organisationName || "");
        // Stored values win; anything absent falls back to the default text
        // with the notification OFF. A notification added to the catalogue
        // after an organisation last saved therefore appears disabled rather
        // than blank.
        const saved = data.settings || {};
        const merged = emptyState();
        for (const n of NOTIFICATIONS) {
          const s = saved[n.key];
          if (!s) continue;
          merged[n.key] = {
            enabled: !!s.enabled,
            text: typeof s.text === "string" && s.text.trim() ? s.text : n.defaultText
          };
        }
        setRows(merged);
        loadedRef.current = true;
        setStatus("ready");
      } catch (e) { setError(e.message); setStatus("error"); }
    })();
  }, []);

  const setRow = (key, patch) => {
    if (!dirtyRef.current) dirtyRef.current = true;
    if (onDirty) onDirty();
    setRows(r => ({ ...r, [key]: { ...r[key], ...patch } }));
  };

  // The actual write. Shared by the standalone button and the parent dialog.
  const commit = async () => {
    const v = valuesRef.current;
    const settings = {};
    for (const n of NOTIFICATIONS) {
      const r = v.rows[n.key] || {};
      settings[n.key] = {
        enabled: !!r.enabled,
        // Never store an empty message — an enabled notification with no
        // text would send a blank push.
        text: (r.text || "").trim() || n.defaultText
      };
    }
    const isAll = (localStorage.getItem("organisationName") || "") === "Pharmace all";
    const body = { settings, ...(isAll ? { organisationName: v.org } : {}) };
    const res = await fetch(`${API}/api/headoffice/communications/save`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    dirtyRef.current = false;
  };

  const save = async () => {
    setStatus("saving"); setError(null);
    try {
      await commit();
      setStatus("saved");
      setTimeout(() => setStatus("ready"), 1800);
    } catch (e) { setError(e.message); setStatus("error"); }
  };

  // Parent-driven save. Unlike the button above this RE-THROWS, so the Settings
  // dialog can report the failure alongside its other save errors.
  useImperativeHandle(ref, () => ({
    isDirty: () => dirtyRef.current,
    isLoaded: () => loadedRef.current,
    save: async () => {
      if (!loadedRef.current || !dirtyRef.current) return { skipped: true };
      setStatus("saving"); setError(null);
      try {
        await commit();
        setStatus("ready");
        return { saved: true };
      } catch (e) {
        setError(e.message);
        setStatus("error");
        throw e;
      }
    }
  }), []);

  const anyOn = NOTIFICATIONS.some(n => rows[n.key]?.enabled);

  if (status === "loading") return <div style={{ padding: 24, color: "#6b7280" }}>Loading communication settings…</div>;

  return (
    <div style={embedded ? { padding: 0, maxWidth: 900 } : { padding: "16px 20px", maxWidth: 900 }}>
      {!embedded && <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Staff notifications</h2>}
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
        Automatic messages for <b>{org || "your organisation"}</b>.
      </div>
      {error && <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>Error: {error}</div>}

      {/* NOT LIVE. The settings below save correctly and are read by the
          backend, but no notification is delivered yet: the staff app does not
          receive push messages at present. Firebase is configured; the
          outstanding piece is the per-device FCM token, which will be stored
          with the staff configuration settings. Remove this banner once delivery
          is switched on. (The amber nav button it used to mention is gone —
          this now lives as a tab in the Settings dialog.) */}
      <div style={{
        border: "1px solid #f59e0b", background: "#fffbeb", borderRadius: 8,
        padding: "12px 14px", marginBottom: 16
      }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: "#92400e", marginBottom: 4 }}>
          ⚠ This module is not yet active
        </div>
        <div style={{ fontSize: 12.5, color: "#92400e", lineHeight: 1.5 }}>
          You can configure and save these settings now, and they will be kept — but
          <b> no notifications are being sent</b>. The staff app does not yet receive push
          messages. Delivery will begin once push notifications are enabled in the app; until
          then, treat anything switched on here as a plan rather than an active alert, and do
          not rely on it to tell you that a pharmacist has not arrived.
        </div>
      </div>

      <div style={section}>
        <div style={{ ...label, marginBottom: 6 }}>Staff / Manager push notifications</div>
        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5, marginBottom: 4 }}>
          These are automatic reminders sent to the Pharmace Staff app around the working day —
          nudging someone to end a break, to clock out at their scheduled finish, or to clock in
          when a shift has started without them, and alerting managers when a pharmacist has not
          arrived. Once live, they will be checked by the server against each branch's rota and
          send only when the expected clocking has not happened.
        </div>
        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5, marginBottom: 12 }}>
          Every notification is <b>off until you switch it on</b>, and each one sends at most once
          per person per day, so a member of staff who ignores a reminder is not messaged
          repeatedly. Nothing is sent outside a person's scheduled session. Edit the wording to
          suit your organisation — the placeholders below are replaced with real values when the
          message is sent.
        </div>
        <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", marginBottom: 14 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "#374151", marginBottom: 3 }}>Placeholders</div>
          {Object.entries(TOKEN_HELP).map(([t, d]) => (
            <div key={t} style={{ fontSize: 11.5, color: "#6b7280" }}>
              <code style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 3, padding: "0 4px" }}>{t}</code>
              {" — "}{d}
            </div>
          ))}
        </div>

        {NOTIFICATIONS.map(n => {
          const r = rows[n.key] || { enabled: false, text: n.defaultText };
          const isDefault = (r.text || "").trim() === n.defaultText;
          return (
            <div key={n.key} style={{
              border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 12px", marginBottom: 10,
              background: r.enabled ? "#f0fdfa" : "#fff"
            }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!r.enabled} style={{ marginTop: 3 }}
                  onChange={e => setRow(n.key, { enabled: e.target.checked })} />
                <span style={{ fontSize: 13.5 }}>
                  <b style={{ color: n.audience === "Manager" ? "#b45309" : "#0f766e" }}>{n.audience}</b>
                  {" — "}{n.label}
                </span>
              </label>
              <div style={{ ...help, marginLeft: 24, marginBottom: 6 }}>{n.help}</div>
              <div style={{ marginLeft: 24 }}>
                <textarea
                  value={r.text}
                  onChange={e => setRow(n.key, { text: e.target.value })}
                  rows={2}
                  placeholder={n.defaultText}
                  style={{
                    width: "100%", boxSizing: "border-box", fontSize: 13, lineHeight: 1.45,
                    padding: "7px 9px", border: "1px solid #9ca3af", borderRadius: 4,
                    resize: "vertical", fontFamily: "inherit",
                    background: r.enabled ? "#fff" : "#fafafa", color: r.enabled ? "#111827" : "#6b7280"
                  }} />
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 3, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>
                    Uses {n.tokens.map(t => <code key={t} style={{ marginRight: 4 }}>{t}</code>)}
                  </span>
                  {!isDefault && (
                    <button type="button" onClick={() => setRow(n.key, { text: n.defaultText })}
                      style={{ border: "1px solid #d1d5db", background: "#fff", borderRadius: 4, cursor: "pointer", fontSize: 11, padding: "2px 8px", color: "#6b7280" }}>
                      Reset to default wording
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {!anyOn && (
          <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "7px 10px" }}>
            No notifications are switched on.
          </div>
        )}
      </div>

      {!embedded && (
        <button onClick={save} disabled={status === "saving"}
          style={{ border: "none", background: "#0f766e", color: "#fff", borderRadius: 6, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: status === "saving" ? "wait" : "pointer" }}>
          {status === "saving" ? "Saving…" : status === "saved" ? "✓ Saved" : "Save communication settings"}
        </button>
      )}
      <div style={{ ...help, marginTop: 8 }}>
        {embedded ? "Saved with the dialog's Save changes button. " : ""}
        Settings are saved against your organisation and will be picked up automatically when
        push delivery goes live. Notifications will then be delivered to the Pharmace Staff app;
        staff without the app installed and signed in will receive nothing.
      </div>
    </div>
  );
});

export { StaffNotificationSettings };

// ===========================================================================
// THE COMMUNICATIONS HUB
// ===========================================================================
// Every send point in the app, gathered in one place. The audit behind this
// lives in COMMUNICATIONS-AUDIT.md.
//
// Two rules, from how this was asked for:
//   • Action Centre MOVES here — it is gone from Head Office.
//   • Everything else is DUPLICATED — the original stays exactly where it is,
//     so nobody's existing workflow breaks.
//
// The catalogue below is the whole structure. Each entry declares what it is,
// where it currently lives and how it is reached; the UI is generated from it.
// Restructuring the hub means editing this array, not the rendering code —
// which is the point, because the shape of this section is the next
// conversation, not a settled thing.
//
// `action` is one of:
//   embed    — the real component renders inline here (nothing duplicated,
//              nothing stubbed: this IS the feature). The entry also carries a
//              `component` key, looked up in EMBEDS below — so adding another
//              embedded feature is a row here plus a line in that map, and the
//              renderer never grows another special case.
//   goto     — navigates to the view that owns it (needs `goTo` from App.js)
//   setting  — a flag on the organisation record that a backend job acts on
//   backend  — no UI of its own; listed so the inventory is honest
//
// StaffNotificationSettings below is still EXPORTED — Settings -> Communications
// renders it as its tab — but it is no longer surfaced in this hub.
// ===========================================================================

export const CATEGORIES = [
  { key: "actioncentre", label: "Action centre",     blurb: "Fleet-wide messages to your pharmacies, with read receipts and acknowledgement tracking." },
  // Messages sits second because, like the Action centre, it is something you
  // DO here rather than a signpost to somewhere else. The rest of the rail is
  // an inventory; these two are the working screens.
  { key: "messages",     label: "Messages",          blurb: "Every message sent to an individual member of staff, wherever in the app it was sent from — and a place to send a new one." },
  { key: "staff",        label: "Staff & workforce", blurb: "Messages to your own people — shift reminders, schedules, timesheets." },
  { key: "rota",         label: "Rota & locums",     blurb: "Circulating free sessions, booking confirmations and payment schedules." },
  { key: "appraisals",   label: "Appraisals",        blurb: "Invitations, appraiser forms and summary emails." },
  { key: "reports",      label: "Scheduled reports", blurb: "Recurring reports emailed to the group manager." },
  { key: "compliance",   label: "Safety & compliance", blurb: "MHRA alerts, near-miss escalation and RIDDOR reminders." },
];

export const CATALOGUE = [
  // ---- Action centre (MOVED here from Head Office) ----
  {
    id: "action-centre", category: "actioncentre", action: "embed", component: "action-centre",
    title: "Action centre",
    detail: "Compose a message to one pharmacy or the whole fleet, require acknowledgement, and track who has opened and acknowledged it. Near-miss alerts seed suggested messages.",
    origin: "Moved here from Head Office", status: "live",
  },

  // ---- Messages (per individual member of staff) ----
  {
    id: "staff-messages", category: "messages", action: "embed", component: "staff-messages",
    title: "Messages",
    detail: "One history per member of staff, newest first, showing what was sent, by which method and whether it arrived. Reads the shared staff_messages store, so it covers messages sent from anywhere in the app — not just from this screen.",
    origin: "New — Communications", status: "live",
  },

  // ---- Staff & workforce ----
  {
    id: "schedule-invite", category: "staff", action: "goto", view: "staff",
    title: "Staff schedule self-service invite",
    detail: "Emails one staff member a personal 24-hour link to view and submit their own weekly schedule.",
    origin: "Staff \u2192 clock in/out", status: "live",
    warning: "Sends to one named person at a time. Bulk sending is refused by the server on purpose \u2014 any shortcut added here must keep that.",
  },
  {
    id: "timesheets", category: "staff", action: "setting", settingKey: "send_timesheets",
    title: "Monthly timesheets to the group manager",
    detail: "Sent on the configured day, covering the payroll month. Estimated days are marked and reconciled the following month.",
    origin: "Settings \u2192 Organisation", status: "config",
  },

  // ---- Rota & locums ----
  {
    id: "rota-circulate", category: "rota", action: "goto", view: "pharmacist_rota",
    title: "Circulate free sessions to locums",
    detail: "One personalised email per recipient \u2014 register pharmacists see only their own free sessions, ad-hoc recipients get the full list.",
    origin: "Pharmacist rota", status: "live",
  },
  {
    id: "rota-log", category: "rota", action: "goto", view: "pharmacist_rota",
    title: "Circulation history",
    detail: "Every previous circulation: date, recipients, delivery status, and the exact email each locum received.",
    origin: "Pharmacist rota", status: "live",
  },
  {
    id: "rota-booking", category: "rota", action: "goto", view: "pharmacist_rota",
    title: "Booking confirmation email",
    detail: "Confirms a booked session to the pharmacist at the address in the register.",
    origin: "Pharmacist rota", status: "live",
  },
  {
    id: "rota-schedules", category: "rota", action: "goto", view: "pharmacist_rota",
    title: "Email schedules to pharmacists",
    detail: "Sends each pharmacist their upcoming sessions.",
    origin: "Pharmacist rota", status: "live",
  },
  {
    id: "rota-payment", category: "rota", action: "backend",
    title: "Monthly locum payment schedule to the group manager",
    detail: "Automatic on the 1st, covering the previous month: one spreadsheet per branch, calculated from each locum's rate and the branch's opening hours.",
    origin: "Pharmacist rota, and Settings \u2192 Organisation", status: "config",
    warning: "Stored behind an endpoint scoped to the signed-in organisation, so it can only be switched on from its own pages \u2014 shown here for completeness.",
  },
  {
    id: "rota-cover-failure", category: "rota", action: "backend",
    title: "Cover failure email",
    detail: "Raised when a session cannot be covered.",
    origin: "Pharmacist rota settings", status: "config",
  },

  // ---- Appraisals ----
  {
    id: "appraisal-pre", category: "appraisals", action: "goto", view: "staff",
    title: "Pre-appraisal invitation",
    detail: "Emails the staff member a link to complete their pre-appraisal ahead of the meeting.",
    origin: "Staff \u2192 appraisals", status: "live",
  },
  {
    id: "appraisal-form", category: "appraisals", action: "goto", view: "staff",
    title: "Appraiser form",
    detail: "Sends the appraiser their form for the upcoming appraisal.",
    origin: "Staff \u2192 appraisals", status: "live",
  },
  {
    id: "appraisal-summary", category: "appraisals", action: "goto", view: "staff",
    title: "Appraisal summary email",
    detail: "Shows exactly what will be sent and lets you edit it before sending. Sending again replaces the stored snapshot.",
    origin: "Staff \u2192 appraisals \u2192 Emails & summary", status: "live",
  },

  // ---- Scheduled reports ----
  { id: "report-sim",      category: "reports", action: "setting", settingKey: "report_sim",
    title: "SIM activity", detail: "Recurring SIM activity report to the group manager.", origin: "Settings \u2192 Organisation", status: "config" },
  { id: "report-expiring", category: "reports", action: "setting", settingKey: "report_expiring",
    title: "Expiring items", detail: "Stock approaching expiry.", origin: "Settings \u2192 Organisation", status: "config" },
  { id: "report-slow",     category: "reports", action: "setting", settingKey: "report_slow",
    title: "Slow movers", detail: "Stock not moving within the configured window.", origin: "Settings \u2192 Organisation", status: "config" },
  { id: "report-fridge",   category: "reports", action: "setting", settingKey: "report_fridge",
    title: "Fridge checks", detail: "Fridge temperature compliance.", origin: "Settings \u2192 Organisation", status: "config" },
  { id: "report-expired",  category: "reports", action: "setting", settingKey: "send_expired",
    title: "Monthly removed / expired drugs", detail: "Sent monthly.", origin: "Settings \u2192 Organisation", status: "config" },
  { id: "report-review",   category: "reports", action: "backend",
    title: "Monthly site performance review", detail: "Sent on the 1st to each branch's managers, benchmarking that site anonymously against its peers.",
    origin: "Settings \u2192 Organisation", status: "config",
    warning: "Token-scoped, like the locum payment schedule \u2014 configurable only from its own page." },

  // ---- Safety & compliance ----
  {
    id: "mhra", category: "compliance", action: "goto", view: "head_office",
    title: "MHRA safety alert dispatch",
    detail: "Pushes a live MHRA alert out to the fleet and tracks which sites have actioned it.",
    origin: "Head Office \u2192 MHRA alerts", status: "live",
  },
  {
    id: "nearmiss", category: "compliance", action: "goto", view: "head_office",
    title: "Near-miss alert escalation",
    detail: "Raises near-miss patterns to the affected pharmacy. Also feeds the Action centre's suggested messages.",
    origin: "Head Office \u2192 near miss", status: "partial",
  },
  {
    id: "riddor", category: "compliance", action: "goto", view: "head_office",
    title: "RIDDOR reminders",
    detail: "Chases accidents whose RIDDOR status is still unknown. Each row tracks the date of the last reminder.",
    origin: "Head Office \u2192 accident book", status: "partial",
  },
];

// Component keys -> the real thing. The catalogue stays data; this is the only
// place the hub knows what an embedded entry actually renders.
const EMBEDS = {
  "action-centre":  ActionCentre,
  "staff-messages": StaffMessages,
};

const STATUS = {
  live:    { label: "Live",          bg: "#dcfce7", fg: "#166534" },
  partial: { label: "Partial",       bg: "#e0f2fe", fg: "#075985" },
  config:  { label: "Setting",       bg: "#f1f5f9", fg: "#475569" },
  planned: { label: "Not yet live",  bg: "#fef3c7", fg: "#92400e" },
};

const H = {
  wrap:    { display: "flex", gap: 16, alignItems: "flex-start" },
  rail:    { width: 210, flexShrink: 0, position: "sticky", top: 0 },
  railBtn: (on) => ({
    display: "block", width: "100%", textAlign: "left", cursor: "pointer",
    border: "1px solid " + (on ? "#0f766e" : "#e5e7eb"),
    background: on ? "#f0fdfa" : "#fff", color: on ? "#0f766e" : "#374151",
    fontWeight: on ? 700 : 500, fontSize: 13, padding: "8px 10px",
    borderRadius: 6, marginBottom: 6, fontFamily: "inherit",
  }),
  body:    { flex: 1, minWidth: 0 },
  card:    { border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px",
             marginBottom: 10, background: "#fff" },
  title:   { fontSize: 14, fontWeight: 700, color: "#111827" },
  detail:  { fontSize: 12.5, color: "#4b5563", lineHeight: 1.5, marginTop: 4 },
  origin:  { fontSize: 11, color: "#9ca3af", marginTop: 6 },
  warn:    { fontSize: 11.5, color: "#92400e", background: "#fffbeb",
             border: "1px solid #fde68a", borderRadius: 5, padding: "6px 8px", marginTop: 8, lineHeight: 1.45 },
  pill: (s) => ({ fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 99,
                  background: STATUS[s].bg, color: STATUS[s].fg, whiteSpace: "nowrap" }),
  open:    { border: "1px solid #0f766e", background: "#fff", color: "#0f766e",
             borderRadius: 5, padding: "4px 10px", fontSize: 12, cursor: "pointer",
             fontFamily: "inherit", fontWeight: 600, marginTop: 8 },
};

// ---------------------------------------------------------------------------
// `goTo` comes from App.js. Without it the "Open" buttons have nowhere to send
// anyone, so they explain that rather than silently doing nothing.
// ---------------------------------------------------------------------------
export default function Communications({ goTo }) {
  const [cat, setCat] = useState("actioncentre");
  const active = CATEGORIES.find((c) => c.key === cat) || CATEGORIES[0];
  const items = CATALOGUE.filter((i) => i.category === cat);

  return (
    <div style={{ padding: "16px 20px" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Communications</h2>
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16, maxWidth: 760, lineHeight: 1.5 }}>
        Every message the platform sends, in one place. The Action centre now lives
        here rather than in Head Office; everything else is also reachable from the
        page that owns it, so existing habits still work.
      </div>

      <div style={H.wrap}>
        <div style={H.rail}>
          {CATEGORIES.map((c) => {
            const n = CATALOGUE.filter((i) => i.category === c.key).length;
            return (
              <button key={c.key} style={H.railBtn(c.key === cat)} onClick={() => setCat(c.key)}>
                {c.label}
                <span style={{ float: "right", opacity: 0.6, fontWeight: 500 }}>{n}</span>
              </button>
            );
          })}
        </div>

        <div style={H.body}>
          <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 12, lineHeight: 1.5 }}>
            {active.blurb}
          </div>

          {items.map((i) => {
            // An embedded entry is not described here — it IS here.
            if (i.action === "embed") {
              const Embedded = EMBEDS[i.component];
              if (!Embedded) {
                // A catalogue row pointing at a component that isn't registered
                // is a wiring mistake, not a user-facing state — say so plainly
                // rather than render an empty box someone has to debug.
                return (
                  <div key={i.id} style={{ ...H.card, borderColor: "#fca5a5", background: "#fef2f2" }}>
                    <div style={H.title}>{i.title}</div>
                    <div style={{ ...H.detail, color: "#991b1b" }}>
                      No component registered for "{i.component || "(none)"}" — add it to EMBEDS in
                      Communications.js.
                    </div>
                  </div>
                );
              }
              return (
                <div key={i.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }}>
                  <Embedded />
                </div>
              );
            }
            return (
              <div key={i.id} style={H.card}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={H.title}>{i.title}</div>
                    <div style={H.detail}>{i.detail}</div>
                    <div style={H.origin}>{i.origin}</div>
                  </div>
                  <span style={H.pill(i.status)}>{STATUS[i.status].label}</span>
                </div>
                {i.warning && <div style={H.warn}>{i.warning}</div>}
                {i.action === "goto" && (
                  <button
                    style={H.open}
                    onClick={() =>
                      goTo
                        ? goTo(i.view)
                        : window.alert(
                            "This opens " + i.origin + ".\n\n" +
                            "Navigation isn't wired up yet \u2014 App.js needs to pass goTo into <Communications />."
                          )
                    }
                  >
                    Open {i.origin}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}